/**
 * Agent Eval Infrastructure — V85-4-8 / gaps F3, I8.
 *
 * F3/I8 found the "golden-eval harness" was a single file whose subject
 * was an in-test keyword stub: no per-agent thresholds, no CI tiering,
 * no cache, and no ledger proving every retained agent was even
 * evaluated. This module is the missing plumbing:
 *
 *   - `AGENT_EVAL_REGISTRY` — one explicit threshold per retained agent
 *     (A1, A2, A3, A3b, A4, A5, A6a, A6b, A6c, A7, A8).
 *   - `resolveEvalTier` — smoke (PR) vs full (nightly/release), from an
 *     env var so CI can dial verbosity without code changes.
 *   - `selectCasesForTier` / `verifyRequiredCasesPresent` — smoke tier
 *     still must exercise nonzero cases and never silently skip a case
 *     an agent's acceptance bar declared "required".
 *   - `computeEvalCacheKey` / `getOrRunEval` — cache an eval result by
 *     everything that changes its meaning (fixture set, output schema,
 *     prompt version, model, provider, tool policy, and this module's
 *     own implementation version) so a stale cache can never mask a
 *     real regression; every returned entry is labeled 'cached' or
 *     'fresh'.
 *   - `verifyLedgerCompleteness` — every declared agent must appear in
 *     the release ledger; a missing agent fails the check.
 *   - `postEvalResultToInternalWorkspace` — only a *passing* outcome is
 *     posted, and it's posted through the existing `agent_runs` table
 *     (already a valid self-referential queue target) under a fixed
 *     internal workspace id — never a customer workspace.
 *
 * Pure logic over `IDatabase`; no Cloudflare/wrangler import
 * (check-imports safe — matches every other packages/core service).
 */
import { sha256Hex, canonicalJson } from '../utils/audit-crypto.js';
import { startAgentRun, completeAgentRun, failAgentRun } from './agent-run-service.js';
/**
 * One row per retained agent (park-listed agents are intentionally
 * absent — this registry IS the "retained" list for eval purposes).
 * Thresholds mirror each agent's build-list acceptance bar where one
 * was stated (V85-4-1..7); agents without a stated numeric bar get a
 * conservative default that a real eval must actually clear.
 */
export const AGENT_EVAL_REGISTRY = [
    { agent_id: 'A1', label: 'Intake elicitation', threshold: 0.9, requiredSmokeCaseIds: ['a1-smoke-1'], minSmokeCases: 1, minFullCases: 20 },
    { agent_id: 'A2', label: 'Rule drafting', threshold: 0.8, requiredSmokeCaseIds: ['a2-smoke-1'], minSmokeCases: 1, minFullCases: 10 },
    { agent_id: 'A3', label: 'Disclosure drafting', threshold: 0.8, requiredSmokeCaseIds: ['a3-smoke-1'], minSmokeCases: 1, minFullCases: 10 },
    { agent_id: 'A3b', label: 'Disclosure Q&A (grounded, no-citation-no-answer)', threshold: 1.0, requiredSmokeCaseIds: ['a3b-smoke-1'], minSmokeCases: 1, minFullCases: 10 },
    { agent_id: 'A4', label: 'Remediation explainer', threshold: 0.8, requiredSmokeCaseIds: ['a4-smoke-1'], minSmokeCases: 1, minFullCases: 10 },
    { agent_id: 'A5', label: 'Drift triage', threshold: 0.8, requiredSmokeCaseIds: ['a5-smoke-1'], minSmokeCases: 1, minFullCases: 10 },
    { agent_id: 'A6a', label: 'Blueprint interview (never asks about an auto-evidenced slot)', threshold: 1.0, requiredSmokeCaseIds: ['a6a-smoke-1'], minSmokeCases: 1, minFullCases: 5 },
    { agent_id: 'A6b', label: 'Blueprint gap options', threshold: 0.8, requiredSmokeCaseIds: ['a6b-smoke-1'], minSmokeCases: 1, minFullCases: 5 },
    { agent_id: 'A6c', label: 'Blueprint board narrative', threshold: 0.8, requiredSmokeCaseIds: ['a6c-smoke-1'], minSmokeCases: 1, minFullCases: 5 },
    { agent_id: 'A7', label: 'Standards watch (exactly one proposal per bump)', threshold: 1.0, requiredSmokeCaseIds: ['a7-smoke-1'], minSmokeCases: 1, minFullCases: 5 },
    { agent_id: 'A8', label: 'Evidence auditor', threshold: 0.9, requiredSmokeCaseIds: ['a8-smoke-1'], minSmokeCases: 1, minFullCases: 10 },
];
export function getAgentEvalDefinition(agentId) {
    return AGENT_EVAL_REGISTRY.find((d) => d.agent_id === agentId);
}
// ---- Tier resolution ----------------------------------------------------
/**
 * Resolve smoke (PR) vs full (nightly/release) from an explicit env
 * map — callers pass `process.env` (or a Workflow's env binding); this
 * module never reads global process state itself so it stays portable.
 */
export function resolveEvalTier(env) {
    const raw = (env.FEDSEC_EVAL_TIER ?? env.EVAL_TIER ?? '').trim().toLowerCase();
    if (raw === 'full' || raw === 'nightly' || raw === 'release')
        return 'full';
    return 'smoke';
}
/**
 * Select which cases a given tier runs. Full tier runs everything.
 * Smoke tier always includes every `requiredSmokeCaseIds` entry, then
 * pads up to `minSmokeCases` with the earliest remaining cases so the
 * "selected tests execute nonzero cases" bar is met even when a suite
 * declares no required cases.
 */
export function selectCasesForTier(definition, allCases, tier) {
    if (tier === 'full')
        return allCases;
    const required = allCases.filter((c) => definition.requiredSmokeCaseIds.includes(c.id));
    const rest = allCases.filter((c) => !definition.requiredSmokeCaseIds.includes(c.id));
    const selected = [...required];
    for (const c of rest) {
        if (selected.length >= definition.minSmokeCases)
            break;
        selected.push(c);
    }
    return selected;
}
/**
 * A required smoke case that got skipped (missing fixture, filtered
 * out by a `-t` pattern, etc.) is a failure, not a quiet no-op.
 */
export function verifyRequiredCasesPresent(definition, selectedCaseIds) {
    const missing = definition.requiredSmokeCaseIds.filter((id) => !selectedCaseIds.includes(id));
    return missing.length === 0 ? { ok: true } : { ok: false, missing };
}
/**
 * A run only passes when it both cleared the score threshold AND ran
 * at least the tier's minimum case count — a suite that quietly runs
 * zero cases (everything filtered/skipped) must not read as a pass.
 */
export function evaluateAgentRun(definition, tier, score, caseCount) {
    const minCases = tier === 'full' ? definition.minFullCases : definition.minSmokeCases;
    const pass = caseCount >= minCases && score >= definition.threshold;
    return { agent_id: definition.agent_id, tier, score, caseCount, pass };
}
// ---- Cache ----------------------------------------------------------------
/**
 * Bump when this module's own scoring/selection logic changes in a way
 * that could change a past result's meaning — a cache entry computed
 * under an older implementation must not be reused as-is.
 */
export const EVAL_INFRASTRUCTURE_VERSION = '1.0.0';
export async function computeEvalCacheKey(input) {
    return sha256Hex(canonicalJson({ ...input, eval_infrastructure_version: EVAL_INFRASTRUCTURE_VERSION }));
}
/** Default in-process cache store; callers may plug in a durable one. */
export class InMemoryEvalCacheStore {
    entries = new Map();
    get(cacheKey) {
        return this.entries.get(cacheKey);
    }
    set(entry) {
        this.entries.set(entry.cache_key, entry);
    }
}
/**
 * Run `runFn` only on a cache miss. Any config change that's part of
 * `EvalCacheKeyInput` (including prompt_version or model_id) changes
 * the key and therefore forces a fresh run — a changed prompt or model
 * can never be served from a stale cache entry.
 */
export async function getOrRunEval(store, keyInput, runFn) {
    const cacheKey = await computeEvalCacheKey(keyInput);
    const existing = await store.get(cacheKey);
    if (existing) {
        return { ...existing, label: 'cached' };
    }
    const outcome = await runFn();
    const entry = {
        cache_key: cacheKey,
        agent_id: keyInput.agent_id,
        outcome,
        computed_at: new Date().toISOString(),
        label: 'fresh',
    };
    await store.set(entry);
    return entry;
}
/**
 * Every agent declared in `AGENT_EVAL_REGISTRY` must have a ledger
 * entry before a release ships — an agent nobody evaluated is not the
 * same as an agent that passed.
 */
export function verifyLedgerCompleteness(registry, ledger) {
    const present = new Set(ledger.map((l) => l.agent_id));
    const missing = registry.filter((d) => !present.has(d.agent_id)).map((d) => d.agent_id);
    return missing.length === 0 ? { ok: true } : { ok: false, missing };
}
// ---- Posting to the internal workspace ----------------------------------
/**
 * Fixed workspace id eval results post under. This is infrastructure
 * telemetry about the product, not a customer's register data — it
 * must never be posted under (or readable from) a customer workspace
 * id, and no customer-facing route resolves this id via `:slug`.
 */
export const INTERNAL_EVAL_WORKSPACE_ID = 'internal-eval-ledger';
/**
 * Post ONE ledger entry's result to the internal workspace's agent-run
 * history. Only a passing outcome is posted — a failing or
 * below-minimum-case outcome must not land in the ledger looking like
 * evidence of health. Reuses `agent_runs` (already a valid
 * self-referential `queue_target`) rather than adding a new table.
 * Returns the created run id, or `null` when nothing was posted.
 */
export async function postEvalResultToInternalWorkspace(db, providerId, entry, keyInput) {
    if (!entry.outcome.pass)
        return null;
    const runId = await startAgentRun(db, {
        agent_id: entry.agent_id,
        workspace_id: INTERNAL_EVAL_WORKSPACE_ID,
        provider: providerId,
        model_id: keyInput.model_id,
        prompt_version: keyInput.prompt_version,
        schema_version: keyInput.schema_version,
        input_hashes_json: { fixture: keyInput.fixture_hash },
        queue_target: 'agent_runs',
    });
    try {
        await completeAgentRun(db, runId, `eval:${entry.agent_id}:${entry.outcome.tier}:${keyInput.implementation_version}`);
    }
    catch (error) {
        await failAgentRun(db, runId, error instanceof Error ? error.message : String(error));
        throw error;
    }
    return runId;
}
//# sourceMappingURL=agent-eval-infrastructure.js.map